import type { FC, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { FlexDivRowCentered } from 'components/layout/flex'
import { MENU_BUTTON_FADE_TIME } from 'constants/menu'
import { linkCSS } from 'styles/common'

type MenuButtonProps = {
	title: string
	isActive: boolean
	icon: ReactNode
	negative?: boolean
	count?: number
	disabled?: boolean
	linkIcon?: ReactNode
	onClick: () => void
}

const MenuButton: FC<MenuButtonProps> = ({
	title,
	icon,
	count,
	isActive,
	onClick,
	linkIcon,
	...props
}) => {
	return (
		<Container onClick={() => !isActive && onClick()} isActive={isActive}>
			<BgOverlay isActive={isActive} />
			<IndicatorContainer>
				<Indicator isActive={isActive} />
			</IndicatorContainer>
			<ButtonContainer>
				<Button isActive={isActive} disabled={props.disabled} negative={props.negative}>
					<Wrapper>
						{icon}
						{title}
					</Wrapper>
				</Button>
				{linkIcon && linkIcon}
				{count !== undefined && count > 0 && <Count>({count})</Count>}
			</ButtonContainer>
		</Container>
	)
}

const ButtonContainer = styled(FlexDivRowCentered)`
	gap: 10px;
	z-index: 20;
`

const Container = styled.div<{ isActive: boolean }>`
	display: flex;
	cursor: ${(props) => (props.isActive ? 'default' : 'pointer')};
	position: relative;
`

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`

const Count = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.sectionHeader};
	font-weight: 700;
	display: flex;
	align-items: center;
	z-index: 10;
`

const Button = styled.div<{ isActive: boolean; disabled?: boolean; negative?: boolean }>`
	${linkCSS};
	position: relative;
	display: flex;
	padding: 20px 0px 20px 20px;
	text-transform: capitalize;
	font-weight: 700;
	opacity: ${(props) => (props.isActive ? 1 : 0.5)};
	color: ${(props) =>
		props.negative
			? props.theme.colors.selectedTheme.newTheme.text.negative
			: props.theme.colors.selectedTheme.newTheme.text.primary};
	&:hover {
		opacity: 1;
		color: ${(props) =>
			props.negative
				? props.theme.colors.selectedTheme.newTheme.text.negative
				: props.theme.colors.selectedTheme.newTheme.text.primary};
	}
	background: transparent;
	& svg {
		fill: ${(props) =>
			props.isActive
				? props.theme.colors.selectedTheme.newTheme.text.primary
				: props.theme.colors.selectedTheme.newTheme.text.secondary};

		path {
			fill: ${(props) =>
				props.negative
					? props.theme.colors.selectedTheme.newTheme.text.negative
					: props.theme.colors.selectedTheme.newTheme.text.secondary};
		}
	}

	${(props) =>
		props.disabled &&
		css`
			color: ${props.theme.colors.selectedTheme.button.disabled.text};
			background: transparent;
			pointer-events: none;
		`}

	transition: opacity ${MENU_BUTTON_FADE_TIME}ms ease-in-out;
`

const Indicator = styled.div<{ isActive: boolean }>`
	width: ${(props) => (props.isActive ? '100%' : '0')};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dashboard.menu.indicator};
	transition: width ${MENU_BUTTON_FADE_TIME * 2}ms ease-in-out;
	z-index: 20;
`

const BgOverlay = styled.div<{ isActive: boolean }>`
	position: absolute;
	z-index: 5;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dashboard.menu.active};
	opacity: ${(props) => (props.isActive ? 1 : 0)};
	transition: opacity ${MENU_BUTTON_FADE_TIME * 2}ms ease-in-out;
	pointer-events: none;
`

const IndicatorContainer = styled.div`
	width: 3px;
	position: relative;
	display: flex;
`

export default MenuButton
